import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Upload,
  Button,
  Select,
  Row,
  Col,
  TimePicker,
  message,
  Alert,
  Space,
  Checkbox,
  Collapse,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import "./RegisterRehabCenter.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const { Option } = Select;

const RegisterRehabCenter = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpVerified, setOtpVerified] = React.useState(false);
  const [otpError, setOtpError] = React.useState("");
  const [otpLoading, setOtpLoading] = React.useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = React.useState(false);
  const [isOpen24Hours, setIsOpen24Hours] = React.useState(false);
  const [pincodeLoading, setPincodeLoading] = React.useState(false);
  const [localities, setLocalities] = React.useState([]);

  // Address autofill similar to pharmacy registration
  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    form.setFieldsValue({ locality: null });
    setLocalities([]);

    if (pincode.length === 6) {
      setPincodeLoading(true);
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          const postOffices = data[0].PostOffice;
          const firstPostOffice = postOffices[0];

          form.setFieldsValue({
            cityTown: firstPostOffice.Block,
            district: firstPostOffice.District,
            state: firstPostOffice.State,
          });

          setLocalities(postOffices);
          if (postOffices.length === 1) {
            form.setFieldsValue({
              locality: firstPostOffice.Name,
            });
          }
        } else {
          message.error("Invalid pincode");
          setLocalities([]);
        }
      } catch (error) {
        message.error("Failed to fetch pincode data. Please try again.");
        setLocalities([]);
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleSendOtp = async () => {
    try {
      const phoneValues = await form.validateFields([
        ["phone", "countryCode"],
        ["phone", "number"],
      ]);
      const phoneNumber = `${phoneValues.phone.number}`;
      setOtpLoading(true);
      setOtpError("");

      const response = await fetch(`${BASE_URL}/otp/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (data.error === "This phone number has already been used.") {
          message.error(data.error);
          setOtpError(data.error);
        } else {
          throw new Error(data.error || "Failed to send OTP");
        }
        return;
      }

      message.success(data.message || "OTP sent successfully!");
      setOtpSent(true);
    } catch (error) {
      setOtpError(error.message || "Failed to send OTP.");
      message.error(error.message || "Failed to send OTP.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const phoneValues = await form.getFieldValue(["phone"]);
      const otpValue = form.getFieldValue("otp");
      if (!otpValue || otpValue.length !== 4) {
        setOtpError("Please enter a valid 4-digit OTP.");
        return;
      }
      const phoneNumber = `${phoneValues.number}`;
      setVerifyOtpLoading(true);
      setOtpError("");

      const response = await fetch(`${BASE_URL}/otp/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phoneNumber, otp: otpValue }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to verify OTP");
      }

      message.success(data.message || "Phone number verified successfully!");
      setOtpVerified(true);
      setOtpSent(false);
    } catch (error) {
      setOtpError(error.message || "OTP verification failed.");
      message.error(error.message || "OTP verification failed.");
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!otpVerified) {
      message.error("Please verify your phone number before submitting.");
      const phoneField = document.getElementById("phone_number");
      if (phoneField) {
        phoneField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (otpSent) {
        const otpInput = document.getElementById("otp");
        if (otpInput) otpInput.focus();
      } else {
        const sendOtpButton = document.getElementById("send-otp-button");
        if (sendOtpButton) sendOtpButton.focus();
      }
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("is_open_24_hours", isOpen24Hours ? "true" : "false");

    if (!isOpen24Hours) {
      const [from, to] = values.openHours || [];
      formData.append("openFrom", from?.format("HH:mm") || "");
      formData.append("openTo", to?.format("HH:mm") || "");
    }
    formData.append("name", values.name);
    formData.append("licenseNumber", values.licenseNumber);
    formData.append("email", values.email);
    formData.append("password", values.password);

    if (values.licenseDoc?.[0]?.originFileObj) {
      formData.append("licenseDoc", values.licenseDoc[0].originFileObj);
    }

    formData.append("addressLine1", values.addressLine1);
    formData.append("locality", values.locality);
    formData.append("cityTown", values.cityTown);
    formData.append("district", values.district);
    formData.append("state", values.state);
    formData.append("pincode", values.pincode);

    if (values.phone) {
      formData.append("phoneCountryCode", values.phone.countryCode);
      formData.append("phoneNumber", values.phone.number);
    }

    if (values.whatsapp) {
      formData.append("whatsappCountryCode", values.whatsapp.countryCode);
      formData.append("whatsappNumber", values.whatsapp.number);
    }

    if (values.googleMapsLink) {
      formData.append("googleMapsLink", values.googleMapsLink);
    }

    if (values.rehabCenterImages && values.rehabCenterImages.length > 0) {
      values.rehabCenterImages.forEach((file) => {
        if (file.originFileObj) {
          formData.append("rehabCenterImages", file.originFileObj);
        }
      });
    }

    formData.append("services", values.services?.join(",") || "");

    try {
      const response = await fetch(`${BASE_URL}/rehab-center/register`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        form.resetFields();
        navigate("/rehab-dashboard", { state: { fromRegistration: true } });
      } else {
        const errorData = await response.json();
        message.error(errorData.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      message.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-rehab-center-container">
      <h2>Register Your Rehab Center with BookLocal</h2>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="register-form"
      >
        <Form.Item
          name="name"
          label="Rehab Center Name"
          rules={[
            { required: true, message: "Please enter the Rehab Center name" },
          ]}
        >
          <Input placeholder="Enter Rehab Center name" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Please enter your password" },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="licenseNumber"
              label="License Number"
              rules={[
                {
                  required: true,
                  message: "Please enter the license number",
                },
              ]}
            >
              <Input placeholder="Enter license number" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name="licenseDoc"
              label="Upload License Document"
              valuePropName="fileList"
              getValueFromEvent={(e) =>
                Array.isArray(e) ? e : e && e.fileList
              }
              rules={[
                {
                  required: true,
                  message: "Please upload the license document",
                },
              ]}
            >
              <Upload
                name="licenseDoc"
                beforeUpload={() => false}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Click to Upload</Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="services"
              label="Services Offered"
              rules={[
                { required: true, message: "Please select services" },
              ]}
            >
              <Select mode="multiple" placeholder="Select services">
                <Option value="rehab">Rehab</Option>
                <Option value="elderly_care">Elderly Care</Option>
                <Option value="post_operative_care">Post Operative Care</Option>
                <Option value="physiotherapy">Physiotherapy</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Collapse style={{ marginBottom: 24 }}>
          <Collapse.Panel
            header={<span style={{ fontWeight: "bold" }}>Center Address</span>}
            key="address"
          >
            <Form.Item
              name="addressLine1"
              label="Address Line 1"
              rules={[
                { required: true, message: "Please enter the street address" },
              ]}
            >
              <Input placeholder="E.g., House No, Street Name" />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="pincode"
                  label="Pincode"
                  rules={[
                    { required: true, message: "Please enter pincode" },
                    {
                      pattern: /^\d{6}$/,
                      message: "Enter valid 6-digit pincode",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter pincode"
                    onChange={handlePincodeChange}
                    maxLength={6}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="locality"
                  label="Locality / Area"
                  rules={[
                    { required: true, message: "Please enter the locality" },
                  ]}
                >
                  <Select
                    placeholder="Select locality"
                    loading={pincodeLoading}
                    disabled={localities.length === 0}
                  >
                    {localities.map((locality) => (
                      <Option key={locality.Name} value={locality.Name}>
                        {locality.Name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="cityTown"
                  label="City / Town"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the city or town",
                    },
                  ]}
                >
                  <Input placeholder="Enter city or town" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="district"
                  label="District"
                  rules={[
                    { required: true, message: "Please enter the district" },
                  ]}
                >
                  <Input placeholder="Enter district" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="state"
                  label="State"
                  rules={[
                    { required: true, message: "Please enter the state" },
                  ]}
                >
                  <Input placeholder="Enter state" />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Form.Item
          name="googleMapsLink"
          label="Google Maps Link"
          rules={[
            { required: true, message: "Please enter the Google Maps link" },
            {
              type: "url",
              message:
                "Please enter a valid Google Maps URL (e.g., https://maps.app.goo.gl/example)",
            },
            {
              pattern:
                /^(https?:\/\/)?(www\.)?(maps\.app\.goo\.gl\/[a-zA-Z0-9]+|[a-zA-Z0-9.-]+\.google\.com\/maps\b)/,
              message: "Please enter a valid Google Maps URL.",
            },
          ]}
        >
          <Input placeholder="Enter Google Maps link (e.g., https://maps.app.goo.gl/example)" />
        </Form.Item>

        <Form.Item
          name="rehabCenterImages"
          label="Upload Rehab Center Images"
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
        >
          <Upload
            name="rehabCenterImages"
            beforeUpload={() => false}
            listType="picture-card"
            multiple
          >
            <div>
              <UploadOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          </Upload>
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Phone Number" required>
              <Input.Group compact>
                <Form.Item
                  name={["phone", "countryCode"]}
                  noStyle
                  initialValue="+91"
                  rules={[
                    { required: true, message: "Country code is required" },
                  ]}
                >
                  <Select style={{ width: "25%" }}>
                    <Option value="+91">+91 (India)</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name={["phone", "number"]}
                  noStyle
                  rules={[
                    { required: true, message: "Please enter phone number" },
                    {
                      pattern: /^\d{10}$/,
                      message: "Enter valid 10-digit phone number",
                    },
                  ]}
                >
                  <Input
                    style={{ width: "75%" }}
                    placeholder="Enter phone number"
                    disabled={otpSent || otpVerified}
                    maxLength={10}
                  />
                </Form.Item>
              </Input.Group>
              {!otpVerified && (
                <Button
                  id="send-otp-button"
                  onClick={handleSendOtp}
                  loading={otpLoading}
                  disabled={otpSent || otpVerified}
                  style={{ marginTop: 8 }}
                >
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </Button>
              )}
              {otpError && (
                <Alert
                  message={otpError}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
              {otpVerified && (
                <Alert
                  message="Phone number verified!"
                  type="success"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
              {!otpVerified &&
                form.isFieldsTouched(true) &&
                !form.getFieldError("phone").length && (
                  <Alert
                    message="Please verify your phone number to submit the registration."
                    type="warning"
                    showIcon
                    style={{ marginTop: 10 }}
                  />
                )}
            </Form.Item>

            {otpSent && !otpVerified && (
              <Form.Item
                label="Enter OTP"
                name="otp"
                rules={[
                  { required: true, message: "Please enter the OTP" },
                  { len: 4, message: "OTP must be 4 digits" },
                ]}
                help={otpError}
                validateStatus={otpError ? "error" : ""}
                className="otp-input-item"
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    placeholder="Enter 4-digit OTP"
                    maxLength={4}
                    className="otp-input"
                  />
                  <Button
                    type="primary"
                    onClick={handleVerifyOtp}
                    loading={verifyOtpLoading}
                  >
                    Verify OTP
                  </Button>
                </Space.Compact>
              </Form.Item>
            )}
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item label="WhatsApp Number" required>
              <Input.Group compact>
                <Form.Item
                  name={["whatsapp", "countryCode"]}
                  noStyle
                  initialValue="+91"
                  rules={[
                    { required: true, message: "Country code is required" },
                  ]}
                >
                  <Select style={{ width: "25%" }}>
                    <Option value="+91">+91 (India)</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name={["whatsapp", "number"]}
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Please enter WhatsApp number",
                    },
                    {
                      pattern: /^\d{10}$/,
                      message: "Enter valid 10-digit WhatsApp number",
                    },
                  ]}
                >
                  <Input
                    style={{ width: "75%" }}
                    placeholder="Enter WhatsApp number"
                    maxLength={10}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="isOpen24Hours"
          valuePropName="checked"
        >
          <Checkbox
            checked={isOpen24Hours}
            onChange={(e) => {
              setIsOpen24Hours(e.target.checked);
              if (e.target.checked) {
                form.setFieldsValue({ openHours: null });
              }
              form.validateFields(["openHours"]);
            }}
          >
            <span style={{ fontWeight: "bold" }}>Open 24 hours?</span>
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="openHours"
          label="Operating Hours"
          rules={[
            {
              required: !isOpen24Hours,
              message: "Please select operating hours",
            },
          ]}
        >
          <TimePicker.RangePicker
            format="h:mm A"
            use12Hours
            minuteStep={30}
            style={{ width: "100%" }}
            disabled={isOpen24Hours}
          />
        </Form.Item>

        <Form.Item
          name="agree"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error("You must agree to continue")),
            },
          ]}
        >
          <Checkbox>
            By Continuing, You Agree To Booklocal’s{" "}
            <a
              href="https://www.booklocal.co.in/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms Of Use
            </a>{" "}
            And Acknowledge Our{" "}
            <a
              href="https://www.booklocal.co.in/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            disabled={
              !otpVerified &&
              form.isFieldsTouched(true) &&
              !form.getFieldError("phone").length
            }
          >
            {loading ? "Submitting..." : "Submit Registration"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default RegisterRehabCenter;